import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  fetchBudgets,
  fetchCentres,
  fetchHqMonthlyBoard,
  reopenConsumables,
  unlockPettyCash
} from 'api/procurement'
import { notify } from 'app/helpers'

import { orderCentres } from '../constants'

// HQ live budget-usage overview: every centre x category, this month. Lists ALL
// centres (from the app-wide centre list) even when no budget is set, so HQ sees
// the full picture. Each budget row already carries used/remaining. Toggle
// between progress bars and plain numbers.
const CATS = [
  { key: 'program_material', label: 'Program Material' },
  { key: 'consumables', label: 'Consumables' },
  { key: 'petty_cash', label: 'Petty Cash' },
  { key: 'repair_maintenance', label: 'Repair & Maintenance' }
]

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
const pct = (used, amt) => (amt > 0 ? Math.min(100, (used / amt) * 100) : 0)
const tone = (used, amt) => {
  if (amt > 0 && used > amt) return '#c62828' // over
  if (amt > 0 && used >= amt * 0.85) return '#f9a825' // near
  return '#2e7d32' // ok
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(1) },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  centre: { fontWeight: 600 },
  num: { fontVariantNumeric: 'tabular-nums' },
  // Tightened so all 4 category columns + Total fit without horizontal scroll.
  cell: { minWidth: 104 },
  // Page-level freeze (P3): header sticks to the top of the screen on scroll,
  // the Centre column sticks to the left.
  stickyTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    },
    '& td': { boxShadow: 'inset 0 -1px 0 #eceae4' }
  },
  frzCentre: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    background: `${theme.palette.background.paper} !important`,
    boxShadow: 'inset 0 -1px 0 #eceae4, 8px 0 8px -8px rgba(0,0,0,.15)'
  },
  frzCentreHead: {
    position: 'sticky',
    left: 0,
    top: 0,
    zIndex: 7,
    boxShadow: 'inset 0 -1px 0 #e0ddd6, 8px 0 8px -8px rgba(0,0,0,.15)'
  }
}))

const HqDashboard = () => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(Dayjs().format('YYYY-MM'))
  const [mode, setMode] = React.useState('bar') // 'bar' | 'number'
  // Category filter: 'all' shows every category column; a single key narrows the
  // table to just that category (Total is hidden then — it would just repeat it).
  const [categoryFilter, setCategoryFilter] = React.useState('all')
  const [rows, setRows] = React.useState([]) // budget rows
  const [board, setBoard] = React.useState([]) // per-centre spend (all categories)
  const [centres, setCentres] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  // One centre selector drives both HQ overrides (keeps the toolbar on one row).
  const [actionCentre, setActionCentre] = React.useState('')
  const [unlocking, setUnlocking] = React.useState(false)
  const [reopening, setReopening] = React.useState(false)
  const [overridesAnchor, setOverridesAnchor] = React.useState(null)

  const monthDate = `${month}-01`

  // HQ reopens a centre's submitted consumables month so they can edit/resubmit.
  const onReopenConsumables = async () => {
    if (!actionCentre) {
      notify('Pick a centre first', '', 'warning')
      return
    }
    setReopening(true)
    try {
      await reopenConsumables(actionCentre, monthDate)
      notify('Consumables reopened for this centre/month', '', 'success')
    } catch (error) {
      notify(error?.message || 'Reopen failed', '', 'error')
    } finally {
      setReopening(false)
    }
  }

  // HQ override: petty cash auto-freezes on the 3rd of the next month; this
  // re-opens a chosen centre's month for edits when they ask.
  const onUnlock = async () => {
    if (!actionCentre) {
      notify('Pick a centre first', '', 'warning')
      return
    }
    setUnlocking(true)
    try {
      await unlockPettyCash(actionCentre, monthDate)
      notify('Petty cash unlocked for this centre/month', '', 'success')
    } catch (error) {
      notify(error?.message || 'Unlock failed', '', 'error')
    } finally {
      setUnlocking(false)
    }
  }

  React.useEffect(() => {
    let alive = true
    setLoading(true)
    ;(async () => {
      try {
        const [budgetRes, centreRes, boardRes] = await Promise.all([
          fetchBudgets({ month: monthDate }),
          fetchCentres(),
          // Per-centre spend for every category — lets us show actual spend even
          // when no budget row exists for that centre/category (user 2026-07-21).
          fetchHqMonthlyBoard(monthDate).catch(() => ({ results: [] }))
        ])
        const budgets = Array.isArray(budgetRes)
          ? budgetRes
          : budgetRes?.results || []
        const centreList = orderCentres(
          Array.isArray(centreRes) ? centreRes : centreRes?.results || []
        )
        if (alive) {
          setRows(budgets)
          setBoard(boardRes?.results || [])
          setCentres(centreList)
        }
      } catch (e) {
        if (alive) setRows([])
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [monthDate])

  const byCentre = React.useMemo(() => {
    const map = {}
    // seed every centre so all show, even with no budget set this month
    centres.forEach((c) => {
      if (c && c.name) {
        map[c.name] = { name: c.name, cats: {}, amount: 0, used: 0 }
      }
    })
    rows.forEach((b) => {
      const name = b.centre_name || b.centre
      if (!map[name]) map[name] = { name, cats: {}, amount: 0, used: 0 }
      const amount = Number(b.amount || 0)
      const used = Number(b.used || 0)
      map[name].cats[b.category] = { amount, used }
      map[name].amount += amount
      map[name].used += used
    })
    // Surface actual spend even where no budget is set: pull each centre's
    // per-category spend from the monthly board and fill in any category that a
    // budget row didn't already cover (shows as "₹spend / ₹0").
    board.forEach((br) => {
      const name = br.centre_name || br.centre
      if (!map[name]) map[name] = { name, cats: {}, amount: 0, used: 0 }
      const spend = {
        program_material: Number((br.pm || {}).total || 0),
        consumables: Number((br.consumables || {}).total || 0),
        petty_cash: Number((br.petty_cash || {}).spent || 0),
        repair_maintenance: Number((br.rm || {}).spent || 0)
      }
      CATS.forEach(({ key }) => {
        const s = spend[key]
        if (!map[name].cats[key] && s > 0) {
          map[name].cats[key] = { amount: 0, used: s }
          map[name].used += s
        }
      })
    })
    return orderCentres(Object.values(map))
  }, [centres, rows, board])

  const renderCell = (c) => {
    const amount = c ? Number(c.amount || 0) : 0
    const used = c ? Number(c.used || 0) : 0
    // Uniform: no budget AND no spend -> a dash everywhere, whether or not a
    // zero-amount budget row happens to exist. (Spend with no budget reads as a
    // full/over bar.) This keeps every 0/0 cell looking the same for HQ.
    if (amount <= 0 && used <= 0) {
      return (
        <Typography variant="caption" color="textSecondary">
          —
        </Typography>
      )
    }
    if (mode === 'number') {
      return (
        <span className={classes.num} style={{ color: tone(used, amount) }}>
          {inr(used)} / {inr(amount)}
        </span>
      )
    }
    return (
      <Box>
        <LinearProgress
          variant="determinate"
          value={amount > 0 ? pct(used, amount) : 100}
          style={{ height: 8, borderRadius: 4, marginBottom: 2 }}
        />
        <Typography variant="caption" style={{ color: tone(used, amount) }}>
          {inr(used)} / {inr(amount)}
        </Typography>
      </Box>
    )
  }

  const shownCats =
    categoryFilter === 'all'
      ? CATS
      : CATS.filter((c) => c.key === categoryFilter)

  return (
    <Box className={classes.root}>
      {/* Slim single row (user 2026-07-21): the month picker and controls ride
          the top-right so the table starts higher; the category filter was
          dropped and the HQ overrides collapsed behind one button + popup. */}
      <div className={classes.toolbar}>
        <div className={classes.spacer} />
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => setMode((m) => (m === 'bar' ? 'number' : 'bar'))}>
          {mode === 'bar' ? 'Numbers' : 'Bars'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setOverridesAnchor(e.currentTarget)}>
          HQ overrides ▾
        </Button>
      </div>

      {/* HQ overrides for the chosen centre + month: unlock an auto-frozen petty
          cash month, or reopen a submitted consumables month for resubmission. */}
      <Popover
        open={!!overridesAnchor}
        anchorEl={overridesAnchor}
        onClose={() => setOverridesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box style={{ padding: 16, width: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="subtitle2">HQ overrides</Typography>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Centre"
            fullWidth
            value={actionCentre}
            onChange={(e) => setActionCentre(e.target.value)}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            size="small"
            disabled={unlocking || !actionCentre}
            onClick={onUnlock}>
            {unlocking ? 'Unlocking…' : 'Unlock petty cash'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={reopening || !actionCentre}
            onClick={onReopenConsumables}>
            {reopening ? 'Reopening…' : 'Reopen consumables'}
          </Button>
        </Box>
      </Popover>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isEmpty(byCentre) ? (
        <Typography variant="body2" color="textSecondary">
          No centres found.
        </Typography>
      ) : (
        <Table size="small" className={classes.stickyTable}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.frzCentreHead}>Centre</TableCell>
              {shownCats.map((c) => (
                <TableCell key={c.key} className={classes.cell}>
                  {c.label}
                </TableCell>
              ))}
              {categoryFilter === 'all' && (
                <TableCell className={classes.cell}>Total</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {byCentre.map((row) => (
              <TableRow key={row.name} hover>
                <TableCell className={`${classes.centre} ${classes.frzCentre}`}>{row.name}</TableCell>
                {shownCats.map((c) => (
                  <TableCell key={c.key}>{renderCell(row.cats[c.key])}</TableCell>
                ))}
                {categoryFilter === 'all' && (
                  <TableCell>
                    {renderCell({ used: row.used, amount: row.amount })}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default HqDashboard
